import { useState, useMemo } from "react";
import { ListNumbers, Warning } from "@phosphor-icons/react";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useTrackOnce } from "@/hooks/useTrackOnce";
import { useSeo } from "@/lib/seo";

const TIME_RE = /(\d{1,2}):(\d{1,2})(?::(\d{2}))?/;

function parseChapters(text) {
  const chapters = [];
  for (const line of text.split("\n")) {
    const m = line.match(TIME_RE);
    if (!m) continue;
    let secs;
    if (m[3] !== undefined) secs = (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]);
    else secs = (+m[1]) * 60 + (+m[2]);
    const label = line.replace(m[0], "").replace(/^[\s\-–—:.]+|[\s\-–—:.]+$/g, "").trim();
    if (label) chapters.push({ secs, label });
  }
  chapters.sort((a, b) => a.secs - b.secs);
  return chapters;
}

const fmtTs = (secs) => {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function ChapterFormatter() {
  useSeo("Video Chapter Formatter", "Paste rough notes with times and get perfectly formatted YouTube chapters — 00:00 start, sorted, ready to paste.");
  const [input, setInput] = useState("");
  const track = useTrackOnce("chapter_formatter");

  const { output, warnings } = useMemo(() => {
    const chapters = parseChapters(input);
    if (!chapters.length) return { output: "", warnings: [] };
    const warns = [];
    let autoIntro = false;
    if (chapters[0].secs !== 0) {
      chapters.unshift({ secs: 0, label: "Intro" });
      autoIntro = true;
      warns.push('YouTube requires the first chapter to start at 00:00 — added an "Intro" chapter automatically.');
    }
    if (chapters.length < 3) warns.push(`YouTube needs at least 3 chapters — you have ${chapters.length}. Add ${3 - chapters.length} more.`);
    for (let i = 1; i < chapters.length; i++) {
      if (i === 1 && autoIntro) continue;
      if (chapters[i].secs - chapters[i - 1].secs < 10) {
        warns.push(`"${chapters[i].label}" starts less than 10 seconds after the previous chapter — YouTube requires 10s minimum.`);
      }
    }
    return { output: chapters.map((c) => `${fmtTs(c.secs)} ${c.label}`).join("\n"), warnings: warns };
  }, [input]);

  const onChange = (v) => {
    setInput(v);
    if (v.trim().length > 8) track();
  };

  return (
    <ToolShell slug="chapter-formatter" title="Video Chapter Formatter" tagline="Type rough notes with times in any format — get YouTube-ready chapters, sorted and starting at 00:00." hasResults={!!output}>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface rounded-[20px] p-6 glow-ring">
          <label className="text-sm font-semibold block mb-2">Rough notes</label>
          <textarea
            data-testid="chapter-input"
            value={input}
            onChange={(e) => onChange(e.target.value)}
            rows={10}
            placeholder={"intro 0:00\n1:23 - setting up the camera\ntalking about lighting 3:45\n12:05 final results"}
            className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)] resize-y font-mono"
          />
        </div>
        <div className="surface rounded-[20px] p-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold flex items-center gap-2"><ListNumbers size={16} color="var(--success)" weight="duotone" /> YouTube-ready</label>
            {output && <CopyButton text={output} label="Copy" testId="copy-chapters-btn" className="!px-3 !py-1.5 !text-xs" />}
          </div>
          <pre data-testid="chapter-output" className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm min-h-[220px] whitespace-pre-wrap font-mono">
            {output || <span className="text-[var(--muted)] font-sans">Formatted chapters appear here…</span>}
          </pre>
        </div>
      </div>
      {warnings.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="chapter-warnings">
          {warnings.map((w, i) => (
            <p key={w} className="flex items-start gap-2 text-sm text-[#F5A623]">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" /> {w}
            </p>
          ))}
        </div>
      )}
    </ToolShell>
  );
}
